import type { ItemId } from '@/data/items';
import { parseSprite, rasterise } from '@/render/spriteBaker';
import { TOOL_ICONS } from '@/sprites/toolIcons';
import type { ToolBelt, ToolDefinition, ToolFamily } from '@/world/toolbelt';

/**
 * What Katie is holding, and how to change it.
 *
 * Third in the top-right cluster beside the bag and the settings cog. Tools
 * left the hotbar when the belt landed, which left the belt with no on-screen
 * control at all: swapping to the rod meant opening the gear screen, finding
 * the Tools tab and closing it again, or knowing there was a keyboard shortcut.
 * On a phone there is no keyboard, so it meant the menu every time.
 *
 * The button always shows the tool that will actually act, so "what am I
 * holding?" is answered without opening anything.
 */

export interface ToolButtonOptions {
  readonly container: HTMLElement;
  readonly belt: ToolBelt;
  readonly onChoose: (id: ItemId) => void;
}

/** Icon pixels are identical every boot, so they are rasterised once. */
const ICON_URLS: Partial<Record<ToolFamily, string>> = {};

function iconFor(family: ToolFamily): string {
  const cached = ICON_URLS[family];
  if (cached) return cached;
  const url = rasterise(parseSprite(TOOL_ICONS[family])).toDataURL();
  ICON_URLS[family] = url;
  return url;
}

export class ToolButton {
  private readonly root: HTMLButtonElement;
  private readonly icon: HTMLImageElement;
  private readonly picker: HTMLDivElement;
  private readonly onDocumentPointerDown: (event: PointerEvent) => void;
  private readonly onKeyDown: (event: KeyboardEvent) => void;
  private open = false;
  private lastSignature = '';

  constructor(private readonly options: ToolButtonOptions) {
    this.root = document.createElement('button');
    this.root.className = 'tool-open';
    this.root.type = 'button';
    this.root.setAttribute('aria-haspopup', 'true');
    this.root.setAttribute('aria-expanded', 'false');

    this.icon = document.createElement('img');
    this.icon.className = 'tool-icon';
    this.icon.alt = '';
    this.root.appendChild(this.icon);

    this.picker = document.createElement('div');
    this.picker.className = 'tool-picker';
    this.picker.setAttribute('role', 'listbox');
    this.picker.hidden = true;

    this.root.addEventListener('click', () => {
      this.setOpen(!this.open);
    });

    // A tap anywhere else dismisses it. Without this the only way out of an
    // accidentally-opened picker is to pick something, which means changing
    // tools to stop changing tools.
    this.onDocumentPointerDown = (event: PointerEvent): void => {
      if (!this.open) return;
      const target = event.target;
      if (target instanceof Node && (this.root.contains(target) || this.picker.contains(target))) {
        return;
      }
      this.setOpen(false);
    };
    document.addEventListener('pointerdown', this.onDocumentPointerDown);

    this.onKeyDown = (event: KeyboardEvent): void => {
      if (this.open && event.key === 'Escape') this.setOpen(false);
    };
    document.addEventListener('keydown', this.onKeyDown);

    options.container.append(this.root, this.picker);
    this.refresh();
  }

  get isOpen(): boolean {
    return this.open;
  }

  setOpen(open: boolean): void {
    this.open = open;
    this.picker.hidden = !open;
    this.root.classList.toggle('is-open', open);
    this.root.setAttribute('aria-expanded', String(open));
    if (open) this.refresh();
  }

  /**
   * Called every frame. Rebuilding on each one would thrash layout for
   * something that changes a few times a minute, so the belt is fingerprinted
   * and the DOM only touched when that changes — the belt also moves via the
   * keyboard shortcut and the gear screen, so polling is what keeps those in
   * step without every caller having to remember to notify this button.
   */
  update(belt: ToolBelt): void {
    const signature = `${belt.activeId ?? '-'}|${belt
      .all()
      .map((tool) => tool.id)
      .join(',')}`;
    if (signature === this.lastSignature) return;
    this.lastSignature = signature;
    this.refresh();
  }

  private refresh(): void {
    const { belt } = this.options;
    const active = belt.activeTool;

    if (active) {
      this.icon.src = iconFor(active.family);
      this.icon.hidden = false;
      this.root.setAttribute('aria-label', `Holding ${active.name}. Change tool`);
    } else {
      this.icon.hidden = true;
      this.root.setAttribute('aria-label', 'No tool in hand. Change tool');
    }

    this.picker.replaceChildren();
    for (const tool of belt.all()) {
      this.picker.appendChild(this.createOption(tool, tool.id === belt.activeId));
    }
  }

  private createOption(tool: ToolDefinition, chosen: boolean): HTMLButtonElement {
    const option = document.createElement('button');
    option.className = 'tool-option';
    option.type = 'button';
    option.setAttribute('role', 'option');
    option.setAttribute('aria-selected', String(chosen));
    option.classList.toggle('is-chosen', chosen);

    const icon = document.createElement('img');
    icon.className = 'tool-option-icon';
    icon.src = iconFor(tool.family);
    icon.alt = '';

    const name = document.createElement('span');
    name.className = 'tool-option-name';
    name.textContent = tool.name;

    option.append(icon, name);
    option.addEventListener('click', () => {
      this.options.onChoose(tool.id);
      this.setOpen(false);
      this.refresh();
    });

    return option;
  }

  destroy(): void {
    document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    document.removeEventListener('keydown', this.onKeyDown);
    this.root.remove();
    this.picker.remove();
  }
}
